import { useMemo, useState } from 'react';

import {
  AngleDown,
  AutoComplete,
  AutoCompleteMenuStatus,
  ChakraFlex,
  ChakraFormControl,
  ChakraFormLabel,
  ChakraText,
  Close,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  IconButton,
  UserAvatarCard,
} from '@equip.health/ui';

import { getTaskAssigneeName } from '~/lib/util/patient.util';

const { label } = EquipUITheme.typography;
const { neutral, primary } = EquipUIFireflyV2Theme.colors;

type FamilyTaskAsssigneeSelectionProps = {
  assignees?: FamilyTaskAssignee[] | null;
  familytaskAssignees: FamilyTaskAssignee[];
  hideLabel?: boolean;
  id: string;
  isLoading: boolean;
  isNonEditable?: boolean;
  updateAssignee: (newAssignee: FamilyTaskAssignee, removed?: boolean) => void;
};

const FamilyTaskAsssigneeSelection = ({
  assignees,
  familytaskAssignees,
  hideLabel = false,
  id,
  isLoading,
  isNonEditable = false,
  updateAssignee,
}: FamilyTaskAsssigneeSelectionProps) => {
  const TOP_LEVEL_ID = `${id}-family-task-assignee`;

  // Initially show the autocomplete only when the assignee is not selected already
  const [showAutoComplete, setShowAutoComplete] = useState<boolean>(!assignees);
  const [lastMenuStatus, setLastMenuStatus] = useState<AutoCompleteMenuStatus>(
    AutoCompleteMenuStatus.CLOSE,
  );

  const [selectedOption, setSelectedOption] = useState<AutoCompleteOption[]>(
    [],
  );

  const autocompleteOptions = useMemo<AutoCompleteOption[]>(
    () =>
      familytaskAssignees?.map(
        (value: FamilyTaskAssignee): AutoCompleteOption => ({
          primaryText: getTaskAssigneeName(value),
          secondaryText: value?.relationToPatient
            ? [value?.relationToPatient]
            : [],
          value: value.externalId,
        }),
      ) ?? [],
    [familytaskAssignees],
  );

  const handleAssigneeSelect = (
    selectedMenuOption: AutoCompleteOption,
  ): void => {
    const { value } = selectedMenuOption;

    const newAssignee: FamilyTaskAssignee = (familytaskAssignees ?? []).find(
      ({ externalId }: FamilyTaskAssignee) => externalId === value,
    );

    setShowAutoComplete(false);
    setSelectedOption([selectedMenuOption]);
    updateAssignee(newAssignee);
  };

  /**
   * Clear the selection when "X" button is clicked
   */
  const onClearAssigneeClick = (removedAssignee: FamilyTaskAssignee): void => {
    setSelectedOption([]);
    updateAssignee(removedAssignee, true);
    setShowAutoComplete(true);
  };

  const handleMenuOpenCloseStatus = (status: AutoCompleteMenuStatus) => {
    if (lastMenuStatus !== status) {
      setLastMenuStatus(status);
      if (assignees)
        setShowAutoComplete(status === AutoCompleteMenuStatus.OPEN);
    }
  };

  return showAutoComplete ? (
    <AutoComplete
      endIcon={<AngleDown fill={primary[100]} fillOpacity={1} />}
      id={TOP_LEVEL_ID}
      isInitMenuOpen
      isLoading={isLoading}
      isRequired
      onMenuOpenClose={handleMenuOpenCloseStatus}
      onSelect={handleAssigneeSelect}
      options={autocompleteOptions}
      placeholder="Search by name"
      selected={selectedOption}
      title={hideLabel ? undefined : 'Assigned to'}
      toggleListOnEndIconClick
      toggleListOnFocus
      toggleListOnFocusWhenInputExists
    />
  ) : (
    <>
      <ChakraFormControl isRequired>
        {!hideLabel && (
          <ChakraFormLabel
            {...label}
            color="neutral.secondary"
            marginInlineStart="unset"
            marginLeft="14px !important"
          >
            Assigned to
          </ChakraFormLabel>
        )}
      </ChakraFormControl>
      <ChakraFlex
        alignItems="center"
        background="rgba(0, 0, 0, 0.03)"
        borderRadius="16px"
        cursor="pointer"
        justifyContent="flex-start"
        marginTop="0px !important"
        minHeight="64px"
        onClick={() => {
          if (!isNonEditable) setShowAutoComplete(true);
        }}
        paddingX="16px"
      >
        {assignees &&
          assignees.length > 0 &&
          assignees.map((elem: FamilyTaskAssignee) => (
            <ChakraFlex
              alignItems="center"
              justifyContent="space-around"
              key={elem?.externalId}
              paddingX="16px"
            >
              <UserAvatarCard
                id={TOP_LEVEL_ID}
                primaryText={[getTaskAssigneeName(elem)]}
                secondaryText={
                  elem?.relationToPatient ? [elem?.relationToPatient] : []
                }
                secondaryTextBold
                width="100px"
              />
              <ChakraText as="span" marginX="8px">
                {!isNonEditable && (
                  <IconButton
                    ariaLabel="clear assignee"
                    icon={<Close fill={neutral.secondary} />}
                    id={`${TOP_LEVEL_ID}__${elem?.externalId}__clear-assignee`}
                    onClick={() => onClearAssigneeClick(elem)}
                    size="sm"
                    variant="ghost"
                  />
                )}
              </ChakraText>
            </ChakraFlex>
          ))}
      </ChakraFlex>
    </>
  );
};

export default FamilyTaskAsssigneeSelection;
